// frontend/src/features/citysense/housing-stock-modeller/components/ScenarioPanel.tsx

import React from 'react';
import { HousingPolygon } from '../types';

interface Props {
  selectedPolygon: HousingPolygon | null;
  archetypes: string[];
  archetypeColors: Map<string, string>;
  onArchetypeChange: (polygonId: string, newArchetype: string) => void;
}

export const ScenarioPanel: React.FC<Props> = ({
  selectedPolygon,
  archetypes,
  archetypeColors,
  onArchetypeChange,
}) => {
  // Fix broken diamond characters
  const cleanArchetypeName = (name: string) => name.replace(/�/g, "◆");

  if (!selectedPolygon) {
    return (
      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-6 text-center">
        <p className="text-gray-600 text-sm">
          👆 <strong>Click a polygon on the map</strong> to change its archetype
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-4">Selected Polygon Editor</h3>
      
      <div className="bg-white rounded-lg p-4 mb-4 space-y-2">
        <div className="flex justify-between">
          <span className="text-sm font-semibold text-gray-600">ID:</span>
          <span className="text-sm text-gray-800">{selectedPolygon.id}</span>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-600">Current Archetype:</span>
          <span className="text-sm text-gray-800 font-bold leading-tight">
            {cleanArchetypeName(selectedPolygon.archetype)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm font-semibold text-gray-600">Dwellings:</span>
          <span className="text-sm text-gray-800">{selectedPolygon.dwellings}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm font-semibold text-gray-600">Houses:</span>
          <span className="text-sm text-gray-800">{selectedPolygon.houses}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm font-semibold text-gray-600">Flats:</span>
          <span className="text-sm text-gray-800">{selectedPolygon.flats}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm font-semibold text-gray-600">Deprivation Index:</span>
          <span className="text-sm text-gray-800">{selectedPolygon.deprivationIndex}</span>
        </div>
      </div>

      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Change Archetype:
      </label>
      <select
        value={selectedPolygon.archetype}
        onChange={(e) => onArchetypeChange(selectedPolygon.id, e.target.value)}
        className="w-full p-3 border-2 border-teal-300 rounded-lg bg-white text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
      >
        {archetypes.map((archetype) => (
          <option key={archetype} value={archetype}>
            {cleanArchetypeName(archetype)}
          </option>
        ))}
      </select>

      <div className="mt-4 grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
        {archetypes.map((archetype) => (
          <button
            key={archetype}
            onClick={() => onArchetypeChange(selectedPolygon.id, archetype)}
            disabled={archetype === selectedPolygon.archetype}
            className={`p-2 text-xs font-bold rounded-lg border-2 transition-all leading-tight ${
              archetype === selectedPolygon.archetype
                ? 'opacity-50 cursor-not-allowed border-gray-400'
                : 'opacity-100 border-white hover:scale-105 shadow-sm'
            }`}
            style={{
              backgroundColor: archetypeColors.get(archetype) || '#ccc',
              color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            }}
          >
            {cleanArchetypeName(archetype)}
          </button>
        ))}
      </div>
    </div>
  );
};